import {
  Flex,
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useCulvertDrainageRenewals,
  useKerbAndChannelDrainageRenewals,
  useOtherDrainageRenewals,
} from '@src/modules/performance/context/reports-api-hooks';
import LineBarChart from '@src/modules/performance/components/charts/LineBarChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Maintenance Activities (Drainage)',
  content: [
    {
      title: 'Drainage renewals - culverts (m)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>A comparison between the planned/forecast length of culvert renewals and the actual lengths achieved.</Text>
    <Text fontSize="inherit" mb={2}>Forecast values are based on the requested levels of funding and Achieved values are based on the approved levels of funding.</Text>
  </>,
      source: null,
    },
    {
      title: 'Drainage renewals - kerb & channel (m)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>A comparison between the planned/forecast length of kerb and channel renewals and the actual lengths achieved.</Text>
    <Text fontSize="inherit" mb={2}>Forecast values are based on the requested levels of funding and Achieved values are based on the approved levels of funding.</Text>
  </>,
      source: null,
    },
    {
      title: 'Drainage renewals - other (m)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>A comparison between the planned/forecast length of other drainage renewals and the actual lengths achieved.</Text>
    <Text fontSize="inherit" mb={2}>Forecast values are based on the requested levels of funding and Achieved values are based on the approved levels of funding.</Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Planned and achieved sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/tio-learning-and-guidance/" isExternal>Transport Investment Online (TIO) learning and guidance.</Link>
  </Text>,
    },
  ],
};

function RoadMaintenanceDeliveryActivitiesDrainage() {
  const { data: culvertDrainageRenewals, isLoading: isLoadingCulvert } = useCulvertDrainageRenewals();
  const { data: kerbAndChannelDrainageRenewals, isLoading: isLoadingKerbAndChannel } = useKerbAndChannelDrainageRenewals();
  const { data: otherDrainageRenewals, isLoading: isLoadingOther } = useOtherDrainageRenewals();
  const isLoading = isLoadingCulvert || isLoadingKerbAndChannel || isLoadingOther;

  // Culvert Drainage Renewals Chart
  const culvertDrainageRenewalsCategories = [...new Set(culvertDrainageRenewals?.map((item) => item.Year))].sort((a, b) => a - b);
  const culvertDrainageRenewalsData = [{
    name: 'Variance %',
    type: 'line',
    data: culvertDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: culvertDrainageRenewals?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Forecast',
    type: 'bar',
    data: culvertDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: culvertDrainageRenewals?.find((item) => item.Year === year)?.Forecast || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Achieved',
    type: 'bar',
    data: culvertDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: culvertDrainageRenewals?.find((item) => item.Year === year)?.Achieved || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  // Kerb and Channel Drainage Renewals Chart
  const kerbAndChannelDrainageRenewalsCategories = [...new Set(kerbAndChannelDrainageRenewals?.map((item) => item.Year))].sort((a, b) => a - b);
  const kerbAndChannelDrainageRenewalsData = [{
    name: 'Variance %',
    type: 'line',
    data: kerbAndChannelDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: kerbAndChannelDrainageRenewals?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Forecast',
    type: 'bar',
    data: kerbAndChannelDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: kerbAndChannelDrainageRenewals?.find((item) => item.Year === year)?.Forecast || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Achieved',
    type: 'bar',
    data: kerbAndChannelDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: kerbAndChannelDrainageRenewals?.find((item) => item.Year === year)?.Achieved || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  // Other Drainage Renewals Chart
  const otherDrainageRenewalsCategories = [...new Set(otherDrainageRenewals?.map((item) => item.Year))].sort((a, b) => a - b);
  const otherDrainageRenewalsData = [{
    name: 'Variance %',
    type: 'line',
    data: otherDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: otherDrainageRenewals?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Forecast',
    type: 'bar',
    data: otherDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: otherDrainageRenewals?.find((item) => item.Year === year)?.Forecast || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Achieved',
    type: 'bar',
    data: otherDrainageRenewalsCategories.map((year) => ({
      x: year,
      y: otherDrainageRenewals?.find((item) => item.Year === year)?.Achieved || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  // Remove Variance series if Forecast or Achieved is null for all years
  if (kerbAndChannelDrainageRenewals && kerbAndChannelDrainageRenewals.every((item) => item.Forecast === null || item.Achieved === null)) {
    kerbAndChannelDrainageRenewals.shift();
  }
  if (culvertDrainageRenewals && culvertDrainageRenewals.every((item) => item.Forecast === null || item.Achieved === null)) {
    culvertDrainageRenewals.shift();
  }
  if (otherDrainageRenewals && otherDrainageRenewals.every((item) => item.Forecast === null || item.Achieved === null)) {
    otherDrainageRenewals.shift();
  }

  return (
    <ReportSection
      title="Road Maintenance Activities (Drainage)"
      isLoading={isLoading}
      lockYear={culvertDrainageRenewals?.[0]?.LockYear}
      informationSource={informationSource}
    >
      <Flex flexDirection="row">
        <LineBarChart
          title="Drainage renewals - culverts (m)"
          categories={culvertDrainageRenewalsCategories}
          series={culvertDrainageRenewalsData}
          width="280"
          height="280"
          leftAxisLabel="Length (m)"
          rightAxisLabel="Variance %"
          lineSeriesName="Variance %"
          barSeriesName="Forecast"
          barSeriesFormatter={(y) => (typeof y !== 'undefined' && y !== null ? `${y.toFixed(0)}m` : '')}
          lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
        />
        <LineBarChart
          title="Drainage renewals - kerb & channel (m)"
          categories={kerbAndChannelDrainageRenewalsCategories}
          series={kerbAndChannelDrainageRenewalsData}
          width="280"
          height="280"
          leftAxisLabel="Length (m)"
          rightAxisLabel="Variance %"
          lineSeriesName="Variance %"
          barSeriesName="Forecast"
          barSeriesFormatter={(y) => (typeof y !== 'undefined' && y !== null ? `${y.toFixed(0)}m` : '')}
          lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
        />
        <LineBarChart
          title="Drainage renewals - other (m)"
          categories={otherDrainageRenewalsCategories}
          series={otherDrainageRenewalsData}
          width="280"
          height="280"
          leftAxisLabel="Length (m)"
          rightAxisLabel="Variance %"
          lineSeriesName="Variance %"
          barSeriesName="Forecast"
          barSeriesFormatter={(y) => (typeof y !== 'undefined' && y !== null ? `${y.toFixed(0)}m` : '')}
          lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
        />
      </Flex>
    </ReportSection>
  );
}

export default RoadMaintenanceDeliveryActivitiesDrainage;
